import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RAMI } from "@/components/PartnerCompanyLinksPanel";
import { Percent, Save, RefreshCw, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function TaxRatesSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  // 🆕 Aggiunta nuovo ramo custom
  const [newRamo, setNewRamo] = useState({ ramo: "", imposte_percent: "", ssn_percent: "" });
  const [adding, setAdding] = useState(false);
  // 🆕 Dialog di modifica esplicita
  const [editingRow, setEditingRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tax-rates");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ramoLabel = (v, isCustom) => {
    if (isCustom) return v.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    return (RAMI.find((r) => r.v === v) || {}).l || v;
  };

  const updateRow = (ramo, key, val) => {
    setRows((prev) => prev.map((r) => r.ramo === ramo ? { ...r, [key]: val, _dirty: true } : r));
  };

  const saveRow = async (row) => {
    setSaving(row.ramo);
    try {
      await api.put("/tax-rates", {
        ramo: row.ramo,
        imposte_percent: parseFloat(row.imposte_percent) || 0,
        ssn_percent: parseFloat(row.ssn_percent) || 0,
      });
      toast.success(`Aliquote ${ramoLabel(row.ramo, row.is_custom)} salvate`);
      setRows((prev) => prev.map((r) => r.ramo === row.ramo ? { ...r, _dirty: false, updated_at: new Date().toISOString() } : r));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  const addNew = async () => {
    const raw = (newRamo.ramo || "").trim();
    if (!raw) { toast.error("Inserisci il nome del ramo"); return; }
    const norm = raw.toLowerCase().replace(/\s+/g, "_");
    if (rows.find((r) => r.ramo === norm)) { toast.error(`Il ramo "${raw}" esiste già`); return; }
    setAdding(true);
    try {
      await api.put("/tax-rates", {
        ramo: norm,
        imposte_percent: parseFloat(newRamo.imposte_percent) || 0,
        ssn_percent: parseFloat(newRamo.ssn_percent) || 0,
      });
      toast.success(`Nuovo ramo "${raw}" aggiunto`);
      setNewRamo({ ramo: "", imposte_percent: "", ssn_percent: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const deleteRow = async (row) => {
    const isCustom = row.is_custom;
    const msg = isCustom
      ? `Eliminare definitivamente il ramo "${ramoLabel(row.ramo, true)}"?`
      : `Il ramo standard "${ramoLabel(row.ramo)}" non può essere eliminato. Vuoi resettarlo a 0% (Imposte + SSN)?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/tax-rates/${encodeURIComponent(row.ramo)}`);
      toast.success(isCustom ? `Ramo "${ramoLabel(row.ramo, true)}" eliminato` : `Ramo "${ramoLabel(row.ramo)}" resettato a 0%`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Apre il dialog "Modifica" per un ramo (con possibilità di rinominare i custom)
  const openEditDialog = (row) => {
    setEditingRow({
      original_ramo: row.ramo,
      ramo: row.ramo,
      imposte_percent: String(row.imposte_percent || ""),
      ssn_percent: String(row.ssn_percent || ""),
      is_custom: row.is_custom,
    });
  };

  const saveEditDialog = async () => {
    if (!editingRow) return;
    const oldRamo = editingRow.original_ramo;
    const newRamoName = (editingRow.ramo || "").trim().toLowerCase().replace(/\s+/g, "_");
    const renamed = editingRow.is_custom && newRamoName && newRamoName !== oldRamo;
    setEditSaving(true);
    try {
      // Se rinominato: crea il nuovo ramo, poi elimina il vecchio
      const target = renamed ? newRamoName : oldRamo;
      await api.put("/tax-rates", {
        ramo: target,
        imposte_percent: parseFloat(editingRow.imposte_percent) || 0,
        ssn_percent: parseFloat(editingRow.ssn_percent) || 0,
      });
      if (renamed) {
        await api.delete(`/tax-rates/${encodeURIComponent(oldRamo)}`);
        toast.success(`Ramo rinominato da "${oldRamo}" a "${newRamoName}" e aliquote aggiornate`);
      } else {
        toast.success(`Aliquote ${ramoLabel(target, editingRow.is_custom)} aggiornate`);
      }
      setEditingRow(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="tax-rates-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Impostazioni agenzia</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Percent className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 bg-blue-100 rounded-lg p-1.5" />
            Configurazione Aliquote
          </h1>
          <p className="text-slate-500 mt-1">Imposte e contributo SSN per ramo. Usate per il calcolo automatico del Totale Lordo nel form polizza.</p>
        </div>
        <Button variant="outline" data-testid="tax-rates-refresh" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Ramo</th>
              <th className="text-left px-4 py-3 w-40">Imposte %</th>
              <th className="text-left px-4 py-3 w-40">SSN %</th>
              <th className="text-left px-4 py-3 w-48">Ultimo aggiornamento</th>
              <th className="text-right px-4 py-3 w-32">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="tax-rates-table">
            {rows.map((r) => (
              <tr key={r.ramo} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tax-rate-row-${r.ramo}`}>
                <td className="px-4 py-3 font-medium">
                  <div className="inline-flex items-center gap-2">
                    {ramoLabel(r.ramo, r.is_custom)}
                    {r.is_custom && <span className="text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">custom</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Input
                    data-testid={`imposte-input-${r.ramo}`}
                    type="number" step="0.01" min="0" max="100"
                    value={r.imposte_percent}
                    onChange={(e) => updateRow(r.ramo, "imposte_percent", e.target.value)}
                    className="h-9 max-w-[120px]"
                  />
                </td>
                <td className="px-4 py-3">
                  <Input
                    data-testid={`ssn-input-${r.ramo}`}
                    type="number" step="0.01" min="0" max="100"
                    value={r.ssn_percent}
                    onChange={(e) => updateRow(r.ramo, "ssn_percent", e.target.value)}
                    className="h-9 max-w-[120px]"
                  />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {r.updated_at ? new Date(r.updated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5">
                    <Button
                      data-testid={`edit-tax-rate-${r.ramo}`}
                      onClick={() => openEditDialog(r)}
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      title={r.is_custom ? "Modifica (rinomina + aliquote)" : "Modifica aliquote"}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`save-tax-rate-${r.ramo}`}
                      onClick={() => saveRow(r)}
                      disabled={saving === r.ramo || !r._dirty}
                      size="sm"
                      className={r._dirty ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                      variant={r._dirty ? "default" : "outline"}
                    >
                      <Save className="h-3.5 w-3.5 mr-1.5" /> {saving === r.ramo ? "…" : "Salva"}
                    </Button>
                    <Button
                      data-testid={`delete-tax-rate-${r.ramo}`}
                      onClick={() => deleteRow(r)}
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      title={r.is_custom ? "Elimina ramo custom" : "Ramo standard: reset a 0%"}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {/* 🆕 Riga aggiungi nuovo ramo custom */}
            <tr className="border-t-2 border-slate-200 bg-blue-50/50" data-testid="tax-rate-new-row">
              <td className="px-4 py-3">
                <Input
                  data-testid="new-ramo-name"
                  placeholder="Nome nuovo ramo (es. Cauzioni)"
                  value={newRamo.ramo}
                  onChange={(e) => setNewRamo({ ...newRamo, ramo: e.target.value })}
                  className="h-9"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  data-testid="new-ramo-imposte"
                  type="number" step="0.01" min="0" max="100"
                  placeholder="0"
                  value={newRamo.imposte_percent}
                  onChange={(e) => setNewRamo({ ...newRamo, imposte_percent: e.target.value })}
                  className="h-9 max-w-[120px]"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  data-testid="new-ramo-ssn"
                  type="number" step="0.01" min="0" max="100"
                  placeholder="0"
                  value={newRamo.ssn_percent}
                  onChange={(e) => setNewRamo({ ...newRamo, ssn_percent: e.target.value })}
                  className="h-9 max-w-[120px]"
                />
              </td>
              <td className="px-4 py-3 text-xs text-slate-400 italic">Nuovo</td>
              <td className="px-4 py-3 text-right">
                <Button
                  data-testid="add-new-ramo-btn"
                  onClick={addNew}
                  disabled={adding || !newRamo.ramo.trim()}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> {adding ? "…" : "Aggiungi"}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
        <p className="font-medium mb-1">💡 Come funziona</p>
        <p>Quando crei o modifichi una polizza, dopo aver inserito l&apos;<strong>Imponibile</strong> (premio netto) e selezionato il <strong>ramo</strong>, l&apos;app applica automaticamente le aliquote qui configurate e compila i campi Imposte, SSN e Totale Lordo. Le aliquote sono per-agenzia (multi-tenant): ogni agenzia ha la sua configurazione indipendente.</p>
      </div>

      {/* 🆕 Dialog di modifica esplicita (rinomina + aliquote) */}
      <Dialog open={!!editingRow} onOpenChange={(v) => !v && setEditingRow(null)}>
        <DialogContent className="max-w-md" data-testid="edit-tax-rate-dialog">
          <DialogHeader>
            <DialogTitle>✏️ Modifica ramo aliquote</DialogTitle>
          </DialogHeader>
          {editingRow && (
            <div className="space-y-3">
              <div>
                <Label>Nome ramo</Label>
                <Input
                  data-testid="edit-tax-ramo-name"
                  value={editingRow.ramo}
                  onChange={(e) => setEditingRow({ ...editingRow, ramo: e.target.value })}
                  disabled={!editingRow.is_custom}
                  className={!editingRow.is_custom ? "bg-slate-100 text-slate-500" : ""}
                />
                {!editingRow.is_custom ? (
                  <p className="text-[11px] text-slate-500 mt-1">I rami <strong>standard</strong> non possono essere rinominati (solo modificare Imposte/SSN).</p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1">Ramo <strong>custom</strong>: puoi rinominarlo. Il nome viene normalizzato (lowercase, underscore).</p>
                )}
              </div>
              <div>
                <Label>Imposte %</Label>
                <Input
                  data-testid="edit-tax-imposte"
                  type="number" step="0.01" min="0" max="100"
                  value={editingRow.imposte_percent}
                  onChange={(e) => setEditingRow({ ...editingRow, imposte_percent: e.target.value })}
                />
              </div>
              <div>
                <Label>SSN %</Label>
                <Input
                  data-testid="edit-tax-ssn"
                  type="number" step="0.01" min="0" max="100"
                  value={editingRow.ssn_percent}
                  onChange={(e) => setEditingRow({ ...editingRow, ssn_percent: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRow(null)}>Annulla</Button>
            <Button
              onClick={saveEditDialog}
              disabled={editSaving || !editingRow?.ramo?.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="edit-tax-rate-save"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" /> {editSaving ? "Salvataggio…" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
